import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { FaMapMarkerAlt, FaPlane, FaHotel, FaClock, FaCalendarAlt, FaFilePdf, FaCheckCircle, FaTimesCircle, FaTimes, FaImages, FaChild } from "react-icons/fa";
import Navbar from "../../components/Navbar/Navbar";
import Topbar from "../../components/Topbar/Topbar";
import Footer from "../../components/Footer/Footer";
import API from "../../API";
import { resolveImageUrl } from "../../constants";
import "./voyageorg.css";

const parseJSON = (data) => {
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    try { return JSON.parse(data); } catch { return []; }
  }
  return [];
};

export default function Voyageorg() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { code } = useParams();
  const [voyageData, setVoyageData] = useState(null);

  const [selectedDepIndex, setSelectedDepIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await API.get(`/voyage-organize/item/${code}`);
        setVoyageData(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    if (code) fetchData();
  }, [code]);

  const departures = voyageData ? parseJSON(voyageData.departures) : [];
  const excursions = voyageData ? parseJSON(voyageData.excursions) : [];
  const excursions_ar = voyageData ? parseJSON(voyageData.excursions_ar) : [];

  const getLocalized = (field, fieldAr) => (isRTL && fieldAr ? fieldAr : field);

  const displayExcursions = isRTL ? excursions_ar || excursions : excursions;

  const durationDays = departures.length > 0
    ? Math.floor((new Date(departures[0].end) - new Date(departures[0].start)) / (1000 * 60 * 60 * 24))
    : 0;
  const durationNights = durationDays > 0 ? durationDays - 1 : 0;

  const selectedDeparture = departures[selectedDepIndex] || null;

  const handleReserve = () => {
    if (!selectedDeparture) return;
    window.location.href = `/reserve/${code}?depart=${selectedDeparture.start}&arrivage=${selectedDeparture.end}`;
  };

  const startPrice = voyageData?.places?.length > 0
    ? Math.min(...voyageData.places.map(p => Number(p.price)))
    : null;

  return (
    <div className={`page-voyageorg ${isRTL ? "rtl" : ""}`}>
      <Topbar />
      <Navbar />

      <div className="vo-content">
        {/* Breadcrumb */}
        <div className="vo-breadcrumb">
          <a href="/">{t("voyageorg.breadcrumb_home")}</a>
          <span className="vo-breadcrumb-sep">&gt;</span>
          <a href="/voyage-organize">{t("voyageorg.breadcrumb_voyage_organize")}</a>
          <span className="vo-breadcrumb-sep">&gt;</span>
          <span className="vo-breadcrumb-current">{voyageData?.title}</span>
        </div>

        {/* Hero: Info Left + Images Right */}
        <div className="vo-hero">
          <div className="vo-hero-info">
            <div className="vo-title-row">
              <h1 className="vo-title">{getLocalized(voyageData?.title, voyageData?.title_ar)}</h1>
              <div className={`vo-availability ${voyageData?.availability ? "available" : "not-available"}`}>
                {voyageData?.availability ? (
                  <><FaCheckCircle /> {t("voyageorg.availability_badge_available")}</>
                ) : (
                  <><FaTimesCircle /> {t("voyageorg.availability_badge_not_available")}</>
                )}
              </div>
            </div>

            <div className="vo-meta-chips">
              {departures.length > 0 && (
                <div className="vo-chip">
                  <FaClock className="vo-chip-icon" />
                  <span>{t("voyageorg.forehead_details.duration", { days: durationDays, nights: durationNights })}</span>
                </div>
              )}
              {voyageData?.airline && (
                <div className="vo-chip">
                  <FaPlane className="vo-chip-icon" />
                  <span>{voyageData.airline}</span>
                </div>
              )}
              {voyageData?.property?.name && (
                <div className="vo-chip">
                  <FaHotel className="vo-chip-icon" />
                  <span>{voyageData.property.name}</span>
                </div>
              )}
            </div>

            {(voyageData?.cite || voyageData?.street) && (
              <div className="vo-location">
                <FaMapMarkerAlt className="vo-location-icon" />
                <span>{voyageData.cite}{voyageData.street ? `, ${voyageData.street}` : ""}</span>
              </div>
            )}

            {getLocalized(voyageData?.description, voyageData?.description_ar) && (
              <p className="vo-hero-description" dir={isRTL ? "rtl" : "ltr"}>
                {getLocalized(voyageData.description, voyageData.description_ar)}
              </p>
            )}

            <div className="vo-hero-bottom">
              {startPrice && (
                <div className="vo-price-block">
                  <span className="vo-price-label">{t("carouselcard.from")}</span>
                  <span className="vo-price-value">{Number(startPrice).toLocaleString()} DA</span>
                  <span className="vo-price-note">{t("carouselcard.perPerson")}</span>
                </div>
              )}

              <div className="vo-hero-actions">
                {voyageData?.availability && departures.length > 0 && (
                  <a href="#tarifs" className="vo-hero-reserve-btn">
                    {t("tarif.reserve_button")}
                  </a>
                )}
                {voyageData?.document && (
                  <a
                    href={resolveImageUrl(voyageData.document)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vo-pdf-link"
                  >
                    <FaFilePdf className="vo-pdf-icon" />
                    {t("presentation.travel_plan_label")}
                  </a>
                )}
              </div>
            </div>

          </div>

          <div className="vo-hero-gallery">
            {voyageData?.images?.length > 0 && (
              <>
                {/* Main image */}
                <div className="vo-gallery-main-wrap" onClick={() => { setGalleryIndex(0); setGalleryOpen(true); }}>
                  <img
                    src={resolveImageUrl(voyageData.images[0].url)}
                    alt={voyageData.title}
                    className="vo-gallery-main"
                  />
                  <div className="vo-gallery-badge">
                    <FaImages /> {voyageData.images.length} {isRTL ? "صور" : "photos"}
                  </div>
                </div>
                {/* Thumbnail row */}
                {voyageData.images.length > 1 && (
                  <div className="vo-gallery-thumbs">
                    {voyageData.images.slice(1, 4).map((img, i) => {
                      const isLast = i === 2 && voyageData.images.length > 4;
                      return (
                        <div
                          key={img.id || i}
                          className="vo-gallery-thumb-wrap"
                          onClick={() => { setGalleryIndex(i + 1); setGalleryOpen(true); }}
                        >
                          <img
                            src={resolveImageUrl(img.url)}
                            alt={`${voyageData.title} ${i + 2}`}
                            className="vo-gallery-thumb"
                          />
                          {isLast && (
                            <div className="vo-gallery-more">
                              <FaImages className="vo-gallery-more-icon" />
                              <span>+{voyageData.images.length - 4}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Excursions Section */}
        {displayExcursions?.length > 0 && (
          <div className="vo-section">
            <h2 className="vo-section-title">{t("voyageorg.tabs.excursions")}</h2>
            <div className="vo-excursions-grid">
              {displayExcursions.map((exc, i) => (
                <div key={i} className="vo-excursion-item">
                  <div className="vo-excursion-number">{i + 1}</div>
                  <span>{typeof exc === 'object' ? exc.name || JSON.stringify(exc) : exc}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dates & Tarifs Section */}
        {voyageData?.availability && departures.length > 0 && (
          <div className="vo-section" id="tarifs">
            <h2 className="vo-section-title">
              <FaCalendarAlt className="vo-section-title-icon" />
              {t("voyageorg.tabs.dates")}
            </h2>
            <p className="vo-dates-hint">{isRTL ? "اختر تاريخ السفر المناسب لك" : "Sélectionnez la date de départ qui vous convient"}</p>
            <div className="vo-date-cards">
              {departures.map((dep, i) => {
                const start = new Date(dep.start);
                const end = new Date(dep.end);
                const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                const nights = days > 0 ? days - 1 : 0;
                const isSelected = selectedDepIndex === i;
                return (
                  <div
                    key={i}
                    className={`vo-date-card ${isSelected ? "selected" : ""}`}
                    onClick={() => setSelectedDepIndex(i)}
                  >
                    {isSelected && <div className="vo-date-card-check"><FaCheckCircle /></div>}
                    <div className="vo-date-card-month">
                      {start.toLocaleDateString(isRTL ? "ar-DZ" : "fr-FR", { month: "short" }).toUpperCase()}
                    </div>
                    <div className="vo-date-card-day">{start.getDate()}</div>
                    <div className="vo-date-card-year">{start.getFullYear()}</div>
                    <div className="vo-date-card-arrow">&darr;</div>
                    <div className="vo-date-card-month">
                      {end.toLocaleDateString(isRTL ? "ar-DZ" : "fr-FR", { month: "short" }).toUpperCase()}
                    </div>
                    <div className="vo-date-card-day">{end.getDate()}</div>
                    <div className="vo-date-card-duration">{days}j / {nights}n</div>
                  </div>
                );
              })}
            </div>

            {/* Reserve button */}
            {selectedDeparture && (
              <button className="vo-main-reserve-btn" onClick={handleReserve}>
                {t("tarif.reserve_button")}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Gallery Lightbox Modal */}
      {galleryOpen && voyageData?.images?.length > 0 && (
        <div className="vo-lightbox" onClick={() => setGalleryOpen(false)}>
          <button className="vo-lightbox-close" onClick={() => setGalleryOpen(false)}>
            <FaTimes />
          </button>
          <div className="vo-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <Swiper
              modules={[Navigation, Pagination]}
              navigation
              pagination={{ clickable: true }}
              initialSlide={galleryIndex}
              loop={voyageData.images.length > 1}
              className="vo-lightbox-swiper"
            >
              {voyageData.images.map((img, i) => (
                <SwiperSlide key={img.id || i}>
                  <img
                    src={resolveImageUrl(img.url)}
                    alt={`${voyageData.title} ${i + 1}`}
                    className="vo-lightbox-img"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="vo-lightbox-counter">
              {galleryIndex + 1} / {voyageData.images.length}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
